import AssistantService from "@/services/AssistantService";
import AssistantReportService from "@/services/AssistantReportService";
import config from "@/configApp";

function buildDebugEvent(event) {
  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    at: new Date().toISOString(),
    type: event?.type || "info",
    title: event?.title || "Evento",
    summary: event?.summary || "",
    payload: event?.payload || {},
  };
}

function buildReportConversationAnswer(data) {
  if (!data) {
    return "Não consegui interpretar a solicitação do relatório.";
  }

  const lines = [];

  if (data.summary) {
    lines.push(data.summary);
  } else if (data.description) {
    lines.push(data.description);
  }

  if (data.title) {
    lines.push(`Estrutura sugerida: ${data.title}.`);
  }

  if (Array.isArray(data.previewItems) && data.previewItems.length) {
    lines.push("Entendi o seguinte para o relatório:");
    data.previewItems.forEach((item) => {
      lines.push(`- ${item}`);
    });
  }

  if (Array.isArray(data.warnings) && data.warnings.length) {
    lines.push("Antes de concluir:");
    data.warnings.forEach((warning) => {
      lines.push(`- ${warning}`);
    });
  } else if (data.status === "preview") {
    lines.push(
      "Se estiver tudo certo, clique em Gerar relatório. Se quiser, continue me orientando para ajustar filtros e colunas."
    );
  }

  return lines.filter((line) => line && line.trim()).join("\n\n");
}

function normalizeReportConversationResponse(result) {
  const normalizedResult = result || {};
  const data = normalizedResult.Data || normalizedResult.data || null;

  normalizedResult.Data = {
    answer: buildReportConversationAnswer(data),
    provider: "",
    model: "",
    usage: {
      promptTokens: null,
      completionTokens: null,
      totalTokens: null,
    },
    warnings: data?.warnings ?? data?.Warnings ?? [],
    suggestedActions: [],
    reportPreview: data,
  };

  return normalizedResult;
}

export const assistant = {
  namespaced: true,
  state: {
    messages: [],
    loading: false,
    error: null,
    lastResponse: null,
    debugEvents: [],
  },
  actions: {
    async askAssistant({ commit, state, rootState }, payload) {
      const request = payload || {};
      const normalizedMode = (request.mode || "help").toLowerCase();
      const assistantConfig = config?.assistant || {};
      const helpDisabled =
        assistantConfig.enabled === false ||
        assistantConfig.helpEnabled === false;
      const reportDisabled =
        assistantConfig.enabled === false ||
        assistantConfig.reportEnabled === false;

      if (
        (normalizedMode === "help" || normalizedMode === "error_explainer") &&
        helpDisabled
      ) {
        const result = {
          Success: false,
          Message: "O assistente de ajuda está temporariamente desativado.",
          Data: null,
        };
        commit("setError", result.Message);
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: "warning",
            title: "Ajuda desativada",
            summary: result.Message,
            payload: {
              mode: normalizedMode,
            },
          })
        );

        return Promise.resolve(result);
      }

      if (normalizedMode === "report" && reportDisabled) {
        const result = {
          Success: false,
          Message:
            "A geração de relatórios com IA está temporariamente desativada.",
          Data: null,
        };
        commit("setError", result.Message);
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: "warning",
            title: "Relatórios com IA desativados",
            summary: result.Message,
            payload: {
              mode: normalizedMode,
            },
          })
        );

        return Promise.resolve(result);
      }

      const messages = Array.isArray(request.messages) ? request.messages : [];
      const appendToHistory = request.appendToHistory !== false;
      const lastMessage =
        messages.length > 0 ? messages[messages.length - 1] : null;

      if (
        appendToHistory &&
        lastMessage &&
        lastMessage.role === "user" &&
        lastMessage.content
      ) {
        const alreadyExists =
          state.messages.length > 0 &&
          state.messages[state.messages.length - 1].role === "user" &&
          state.messages[state.messages.length - 1].content ===
            lastMessage.content;

        if (!alreadyExists) {
          commit("appendMessage", {
            role: "user",
            content: lastMessage.content,
            createdAt: new Date().toISOString(),
          });
        }
      }

      commit("setLoading", true);
      commit("setError", null);
      commit(
        "appendDebugEvent",
        buildDebugEvent({
          type: "request",
          title: "Envio para o assistente",
          summary: `${request.mode || "help"} | ${
            request.screen || "sem tela"
          }`,
          payload: {
            mode: request.mode,
            prompt: request.prompt,
            feature: request.feature,
            context: request.context,
            screen: request.screen,
            route: request.route,
            companyId: request.companyId,
            userRole: request.userRole,
            messages,
          },
        })
      );

      try {
        const currentReportRequest =
          rootState?.assistantReports?.currentRequest || {};
        const currentPreview =
          rootState?.assistantReports?.currentPreview || null;
        const currentReport =
          rootState?.assistantReports?.currentReport || null;

        const result =
          (request.mode || "").toLowerCase() === "report"
            ? normalizeReportConversationResponse(
                await AssistantReportService.GeneratePreview({
                  prompt:
                    request.prompt ||
                    (lastMessage?.content || "").trim() ||
                    messages
                      .filter(
                        (message) =>
                          message?.role === "user" &&
                          (message?.content || "").trim().length > 0
                      )
                      .map((message) => message.content.trim())
                      .join("\n"),
                  context: request.context,
                  reportType: request.reportType || "",
                  dateInitial: request.dateInitial ?? null,
                  dateFinal: request.dateFinal ?? null,
                  models: request.models || ["55", "65"],
                  sources: request.sources || [],
                  filters: request.filters || [],
                  messages,
                })
              )
            : await AssistantService.PostByMode(request.mode, {
                mode: request.mode,
                feature: request.feature,
                context: request.context,
                screen: request.screen,
                route: request.route,
                companyId: request.companyId,
                userRole: request.userRole,
                messages,
              });

        commit("setLastResponse", result.Data || null);
        if ((request.mode || "").toLowerCase() === "report") {
          const preview = result?.Data?.reportPreview || null;

          commit(
            "assistantReports/setCurrentRequest",
            {
              prompt:
                request.prompt ||
                messages
                  .filter(
                    (message) =>
                      message?.role === "user" &&
                      (message?.content || "").trim().length > 0
                  )
                  .map((message) => message.content.trim())
                  .join("\n"),
              context: request.context || "",
              reportType: request.reportType || "",
              dateInitial: preview?.dateInitial ?? request.dateInitial ?? null,
              dateFinal: preview?.dateFinal ?? request.dateFinal ?? null,
              models: request.models || ["55", "65"],
              sources: request.sources || [],
              filters: request.filters || [],
              messages,
              confirmedPlan:
                preview?.confirmedPlan ||
                request.confirmedPlan ||
                currentPreview?.confirmedPlan ||
                currentReport?.confirmedPlan ||
                currentReportRequest?.confirmedPlan ||
                null,
            },
            { root: true }
          );
          commit("assistantReports/setCurrentPreview", preview, { root: true });
          commit("assistantReports/setCurrentReport", null, { root: true });
        }
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: result?.Success === true ? "response" : "warning",
            title: "Retorno do assistente",
            summary: result?.Message || "Resposta recebida",
            payload: result,
          })
        );

        if (result.Success === true && appendToHistory && result.Data?.answer) {
          commit("appendMessage", {
            role: "assistant",
            content: result.Data.answer,
            createdAt: new Date().toISOString(),
          });
        }

        if (result.Success !== true) {
          commit(
            "setError",
            result.Message || "Não foi possível consultar o assistente."
          );
        }

        return Promise.resolve(result);
      } catch (error) {
        const message =
          error?.response?.data?.Message ||
          error?.Message ||
          error?.message ||
          "Não foi possível consultar o assistente.";
        commit("setError", message);
        commit(
          "appendDebugEvent",
          buildDebugEvent({
            type: "error",
            title: "Falha na chamada do assistente",
            summary: message,
            payload: error?.response?.data || error,
          })
        );
        return Promise.reject(error?.response?.data || error);
      } finally {
        commit("setLoading", false);
      }
    },
    clearConversation({ commit }) {
      commit("clearConversation");
    },
    clearError({ commit }) {
      commit("setError", null);
    },
    clearDebugEvents({ commit }) {
      commit("setDebugEvents", []);
    },
  },
  mutations: {
    appendMessage(state, message) {
      state.messages.push(message);

      if (state.messages.length > 30) {
        state.messages.splice(0, state.messages.length - 30);
      }
    },
    clearConversation(state) {
      state.messages = [];
      state.lastResponse = null;
      state.error = null;
      state.debugEvents = [];
    },
    setLoading(state, loading) {
      state.loading = loading;
    },
    setError(state, error) {
      state.error = error;
    },
    setLastResponse(state, response) {
      state.lastResponse = response;
    },
    appendDebugEvent(state, event) {
      state.debugEvents.push(event);

      if (state.debugEvents.length > 100) {
        state.debugEvents.splice(0, state.debugEvents.length - 100);
      }
    },
    setDebugEvents(state, value) {
      state.debugEvents = value || [];
    },
  },
};

export default assistant;
